<template>
    <div>
        <h2
            v-if="tableHeading"
            :id="uniqueId"
        >
            {{ tableHeading }}
        </h2>
        <div
            v-if="tableHasContent"
            class="table-container"
        >
            <table
                :aria-labelledby="tableHeading ? uniqueId : null"
                class="monitori-table"
            >
                <thead>
                    <tr>
                        <th
                            v-for="(header, index) in tableData.header"
                            :key="index"
                            scope="col"
                        >
                            <span>{{ header }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody
                    :key="forceUpdateKey"
                    class="table-data"
                >
                    <tr
                        v-for="(dataset, index) in datasetValuesFromMap"
                        :key="index"
                        role="button"
                        :aria-label="getClickableRowLabel(tableData.header, dataset)"
                        :class="['table-data-row-responsive', 'open-modal-' + index]"
                        tabindex="0"
                        @click="rowClicked(index)"
                        @keydown.enter.prevent="rowClicked(index)"
                        @keydown.up="previousRow(index)"
                        @keydown.down="nextRow(datasetValuesFromMap, index)"
                    >
                        <!-- "Mikäli ehdot täyttyvät, rivin alkuun tulee 'piste'" -->
                        <td
                            v-for="(value, indexDs) in dataset"
                            :key="indexDs"
                            :class="[ showArrow && indexDs === dataset.length - 1 ? 'cell-with-arrow' : '', showDotColumn && isRowHighLighted(dataset) ? 'row-dot' : '' ]"
                        >
                            <span class="column-value">{{ value }}</span>
                            <!-- Mikäli ehdot täyttyvät, lisätään nuoli viimeiseen sarakkeeseen -->
                            <span
                                v-show="showArrow && indexDs === dataset.length - 1"
                                class="arrow"
                            />
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="showFooter">
                    <tr>
                        <td :colspan="columnCount">
                            <div class="footer">
                                <label :for="uniqueId + 1">{{ $t(footerHeader) }}</label>
                                <p :id="uniqueId + 1">
                                    <span
                                        v-if="showFooterLegend"
                                        class="dot"
                                    />
                                    {{ $t(footerText) }}
                                </p>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Emit, Prop, Vue, Watch } from "vue-facing-decorator"
    import TableData from "@/models/tabledata"
    /** html taulukko korostuksilla ja klikkauksen emitoinnilla, käytetään viat ja häiriöt lomakkeella, käyttää tabledata.extendedData */
    import { store } from "@/store/store"

    @Component
    export default class DynamicTableResposive extends Vue {
        uniqueId: string = ""

        @Prop({ type: TableData }) tableHeading: string | undefined
        @Prop({ type: TableData }) tableData: TableData | undefined
        /** Näytetäänkö table footer */
        @Prop({ type: Boolean, default: false }) showFooter!: boolean
        /** Näytetäänkö table footer legendat */
        @Prop({ type: Boolean, default: false }) showFooterLegend!: boolean
        /** Näytetäänkö footerin otsikko/ensimäinen teksti */
        @Prop({ type: String }) footerHeader!: string | undefined
        /** Näytetäänkö footerin leipäteksti */
        @Prop({ type: String }) footerText!: string | undefined
        /** Näytetäänkö dot column = 1. column */
        @Prop({ type: Boolean, default: true }) showDotColumn!: boolean
        /** Näytetäänkö nuoli */
        @Prop({ type: Boolean, default: true }) showArrow!: boolean

        created() {
            this.uniqueId = "id-" + Math.random().toString(16).slice(2)
        }

        /** riviä klikattu */
        @Emit("rowClicked")
        rowClicked(index) {
            if (index !== -1) {
                this.selectedRow = index
            }
            return index
        }

        previousRow(index: number) {
            if (index > 0) {
                (this.$el.querySelectorAll(".table-data-row-responsive")[index - 1] as HTMLTableRowElement).focus()
            }
        }

        nextRow(dataList: string[][], index: number) {
            if (index < dataList.length - 1) {
                (this.$el.querySelectorAll(".table-data-row-responsive")[index + 1] as HTMLTableRowElement).focus()
            }
        }

        // TODO Tpa: onko scrollbar tarpeeksi hyvän näköinen html perus oletuksella? vai pitääkö muotoilla custom näköiseksi
        // TODO Tpa: Nuoli div scrollautuu jostain syystä otsikko rivin päälle...nuoli varmaan pitäisi olla kuva/ikoni eikä border kikkailu

        selectedRow = -1
        forceUpdateKey = 0

        /** Muunnetaan MAP arrayksi, Huom vue 3+ tukisi suoraa iterointia: Tpa */
        get datasetValuesFromMap(): string[][] {
            if (this.tableData?.extendedData != null) {
                // this.forceUpdateKey += 1 // Pakotetaan taulukon päivitys Safarin renderöintiongelman vuoksi // tämä aiheuttaa selaimen crashamaan!!!
                if (this.forceUpdateKey === 0) {
                    this.forceUpdateKey++
                }
                return Array.from(this.tableData?.extendedData.keys())
            }
            return []
        }

        get tableHasContent(): boolean {
            // näytetetään taulukko vain mikäli tabledatasta löytyy extendedData (huom. eri kuin data joka tässä oletuksena null)
            return this.tableData?.extendedData !== null
        }

        // palautetaan sarakkeiden määrä
        get columnCount(): number {
            return this.tableData?.header?.length ?? 0
        }

        get modalValue(): string {
            return store.getters.modalComponentName
        }

        getClickableRowLabel(dataHeaders, dataset): string {
            // Kerrotaan käyttäjälle, mitä tietoa tämä saa taulukon riviä klikkaamalla
            return this.$t("App.Table.AvaaLisatietoja") + " " + dataHeaders[0] + " " + dataset[0] + " " + dataHeaders[1] + " " + dataset[1]
        }

        @Watch("modalValue")
        handleModalCloses(newVal, oldVal) {
            if ((oldVal === "LupaJaRekisterointitoimipaikatModal"
                || oldVal === "PostitoimipisteetModal"
                || oldVal === "RekisterointitoimipaikatModal") && newVal === "") {
                this.$nextTick(() => {
                    (this.$el.querySelectorAll(".open-modal-" + this.selectedRow)[0] as HTMLTableRowElement).focus()
                })
            }
        }

        /** Tarkistetaan onko rivi määritetty korostetuksi */
        isRowHighLighted(item: string[]): boolean | undefined {
            if (this.tableData?.extendedData != null) {
                return this.tableData?.extendedData.get(item)
            }
            return false
        }
    }
</script>

<style scoped>
    .table-container {
        overflow-y: auto;
        /*height: 400px;*/ /*' TODO: minkä korkuinen taulukko? */
        /* height: calc(100vh - 140px - var(--var-footer-height) - var(--var-header-height)); TODO: tarkistaa onko se ok? */
    }

    /** otsikko ja footer pysymään paikallaan kun scrollataan */
    table thead,
    table tfoot {
        position: sticky;
        z-index: 1;
    }
    table thead {
        inset-block-start: 0; /* "top" */
    }
    table tfoot {
        inset-block-end: 0; /* "bottom" */
    }

    .monitori-table {
        border: none;
        border-collapse: collapse;
        width: 100%;
        table-layout: fixed;
    }

    .monitori-table tbody {
       display: table-row-group;
       width: 100%;
       overflow: auto;
       height: 100%;
    }

    .monitori-table thead {
        width: 100%;
        display: table-header-group;
    }

    .monitori-table thead tr {
        width: 100%;
        padding-top: 5px;
        background-color: var(--Traficom-main-color);
        color: white;
        border: none;
    }

    tbody tr:nth-child(even) {
        background-color: #F2F4F8;
    }

    thead th, tbody td {
        width: auto;
        border: none;
        padding: 5px 10px;
        word-break: break-word;
    }

    .cell-with-arrow {
        width: auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    tfoot {
        display: table-footer-group;
    }

    tfoot tr {
        border: none;
    }

    tfoot td {
        border: none;
        padding: 10px 20px 5px 0px;
        word-break: break-word;
        width: auto;
    }

        tfoot label, tfoot p {
            font-size: 0.75rem;
            padding-right: 20px;
        }

    .footer {
        display: flex;
        flex-direction: row;
    }

    .table-data-row-responsive:hover {
        cursor: pointer;
    }

    .table-data-col-dot {
        padding-left: 5px !important;
        padding-right: 5px !important;
        border: none;
        width: 5% !important;
        text-align: center;
        vertical-align: middle;
    }

    .row-dot:first-child::before, .dot::before {
        content: "";
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--Traficom-main-color) 0% 0% no-repeat padding-box;
        margin-right: 5px;
        vertical-align: middle;
    }

    .column-value {
        align-self: flex-start;
    }

    .arrow {
        display: inline-block;
        color: #5E5E5E;
        border-right: 1px solid;
        border-bottom: 1px solid;
        height: 12px;
        width: 12px;
        margin-left: 5px;
        margin-right: 5px;
        padding: 5px;
        transform: rotate(-45deg);
        align-self: center;
    }

    .table-data tr:focus {
        outline: 3px solid var(--Traficom-main-color);
        outline-offset: -4px;
    }
</style>
